import { motion } from 'framer-motion';
import { useReveal } from '../components/useReveal';

const services = [
  {
    num: '01',
    title: 'Web Application Development',
    desc: 'Scalable, production-ready web applications using React.js with REST API integration, database-driven logic, and responsive, performance-optimized interfaces.',
    tags: ['React.js', 'REST API', 'MySQL', 'Performance'],
  },
  {
    num: '02',
    title: 'ERP & Business Systems',
    desc: 'Custom ERP-level management systems designed to streamline business operations — inventory, workflows, reporting, all in a modern web interface.',
    tags: ['Custom ERP', 'Dashboards', 'Data Management'],
  },
  {
    num: '03',
    title: 'E-commerce Development',
    desc: 'Full WooCommerce/WordPress stores built, customized, and optimized — from product setup and payment gateways to store performance and SEO.',
    tags: ['WordPress', 'WooCommerce', 'Payments', 'SEO'],
  },
  {
    num: '04',
    title: 'VPS Deployment & DevOps',
    desc: 'End-to-end server setup, application deployment, and live maintenance on Hostinger VPS. Taking your app from development to a live, stable production environment.',
    tags: ['Hostinger VPS', 'Linux', 'Nginx', 'Deployment'],
  },
  {
    num: '05',
    title: 'Digital Marketing Management',
    desc: 'Full-service digital marketing — social media strategy, content creation, paid ads, and analytics — currently managing 4 active business accounts.',
    tags: ['Social Media', 'Meta Ads', 'Content', 'Analytics'],
  },
  {
    num: '06',
    title: 'UI/UX & Brand Design',
    desc: 'Clean, intuitive interfaces and brand visuals that communicate trust and professionalism — designed for conversion and user engagement.',
    tags: ['Figma', 'Branding', 'Responsive', 'User Flow'],
  },
];

export default function Services() {
  const { ref, controls, variants } = useReveal();

  return (
    <section id="services" style={{ background: 'var(--black)', padding: '130px 80px' }}>

      <motion.div ref={ref} initial="hidden" animate={controls} variants={variants} className="section-header">
        <span className="section-num">02</span>
        <div className="section-line" />
        <h2 className="section-title">My <em>Services</em></h2>
      </motion.div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2px' }} className="services-grid">
        {services.map((svc, i) => (
          <ServiceCard key={svc.num} svc={svc} delay={i * 0.07} />
        ))}
      </div>

      <style>{`
        .services-grid { grid-template-columns: repeat(3, 1fr); }
        @media (max-width: 1100px) { .services-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 700px) { .services-grid { grid-template-columns: 1fr !important; } }
        @media (max-width: 900px) { section#services { padding: 90px 24px !important; } }
      `}</style>
    </section>
  );
}

function ServiceCard({ svc, delay }) {
  const { ref, controls, variants } = useReveal();
  return (
    <motion.div
      ref={ref} initial="hidden" animate={controls}
      variants={{ ...variants, visible: { ...variants.visible, transition: { ...variants.visible.transition, delay } } }}
      style={{ background: 'var(--panel)', padding: '48px 36px', border: '1px solid var(--border)', position: 'relative', overflow: 'hidden', transition: 'border-color .4s', cursor: 'none' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(201,169,110,0.22)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
      data-hover
    >
      {/* Bottom gold bar on hover */}
      <div className={`svc-bar-${svc.num}`} style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '2px', background: '#c9a96e', transform: 'scaleX(0)', transformOrigin: 'left', transition: 'transform .4s ease' }} />

      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '54px', fontWeight: 300, color: 'rgba(201,169,110,0.1)', lineHeight: 1, marginBottom: '28px' }}>{svc.num}</div>
      <div style={{ fontSize: '15px', fontWeight: 600, letterSpacing: '0.03em', marginBottom: '16px' }}>{svc.title}</div>
      <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '17px', fontWeight: 300, color: 'rgba(245,240,232,0.55)', lineHeight: 1.7, marginBottom: '24px' }}>{svc.desc}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {svc.tags.map(t => (
          <span key={t} style={{ fontSize: '9px', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#c9a96e', padding: '3px 10px', border: '1px solid rgba(201,169,110,0.2)', borderRadius: '1px' }}>{t}</span>
        ))}
      </div>

      <style>{`
        div:hover .svc-bar-${svc.num} { transform: scaleX(1) !important; }
      `}</style>
    </motion.div>
  );
}
